import { UploadFileOutlined as UploadFileOutlinedIcon } from '@mui/icons-material'
import { MANUAL_MOVEMENT_OPTIONS } from '../../../../../../../constants/inventoryLabels'
import SectionCard from '../../components/SectionCard'
import { stockOperationModes } from '../../constants/inventoryModes'
import {
  getBaseUnitLabel,
  getDefaultInputUnit,
  getInputUnitOptions
} from '../../utils/inventoryUnits'

const OperationsSection = ({ actions, data, navigation, state }) => {
  const selectedManualItemOption =
    data.stockOperationItemOptions.find(option => String(option.id) === String(state.manualForm.itemId)) || null
  const selectedTransferItemOption =
    data.stockOperationItemOptions.find(option => String(option.id) === String(state.transferForm.itemId)) || null

  const manualUnitOptions = getInputUnitOptions(selectedManualItemOption?.unit || '')
  const transferUnitOptions = getInputUnitOptions(selectedTransferItemOption?.unit || '')
  const manualQtyUnit = state.manualForm.qtyUnit || getDefaultInputUnit(selectedManualItemOption?.unit || '')
  const transferQtyUnit =
    state.transferForm.qtyUnit || getDefaultInputUnit(selectedTransferItemOption?.unit || '')

  return (
    <SectionCard
      title='Mantención de inventario'
      description='Gestiona movimientos manuales y transferencias desde una sola pantalla operativa.'
    >
      <div className='mb-4 flex flex-col gap-3'>
      <div className='flex flex-col gap-3 rounded-2xl border border-accent2/20 bg-accent2/[0.08] px-4 py-3 md:flex-row md:items-center md:justify-between'>
        <div>
          <p className='text-xs font-semibold uppercase tracking-[0.14em] text-accent2'>Actualizar stock</p>
          <p className='mt-1 text-sm text-slate-600'>
            Usa la carga Excel para regularizar existencias masivas o cargar stock inicial.
          </p>
        </div>

        <button
          type='button'
          onClick={() => navigation.openUploadModal('initial-stock')}
          className='inline-flex items-center justify-center gap-1.5 rounded-lg border border-accent2/25 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-[0_8px_16px_rgba(15,23,42,0.06)] transition hover:border-accent2/40 hover:bg-accent2/[0.06] hover:text-primary'
        >
          <UploadFileOutlinedIcon style={{ fontSize: '1rem' }} className='text-accent2' />
          Revisar carga Excel
        </button>
      </div>

      <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
        {stockOperationModes.map(mode => {
          const IconComponent = mode.icon
          const isActiveMode = navigation.activeOperationMode === mode.key

          return (
            <button
              key={mode.key}
              type='button'
              onClick={() => actions.navigateToSection('operations', mode.key)}
              className={`rounded-xl border px-3 py-3 text-left transition ${
                isActiveMode
                  ? 'border-accent2/40 bg-accent2/[0.08] shadow-[0_10px_24px_rgba(28,178,175,0.12)]'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className='flex items-start gap-3'>
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-lg border ${
                    isActiveMode
                      ? 'border-accent2/20 bg-accent2/[0.14] text-accent2'
                      : 'border-slate-200 bg-slate-50 text-slate-500'
                  }`}
                >
                  <IconComponent style={{ fontSize: '1rem' }} />
                </div>
                <div>
                  <p className={`text-sm font-semibold ${isActiveMode ? 'text-text' : 'text-slate-700'}`}>
                    {mode.label}
                  </p>
                  <p className='mt-1 text-xs text-slate-500'>{mode.description}</p>
                </div>
              </div>
            </button>
          )
        })}
      </div>
      </div>

      {navigation.activeOperationMode === 'manual' ? (
        <form className='space-y-3' onSubmit={actions.handleManualMovementSubmit}>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <div>
            <label className='block text-xs font-semibold text-slate-700 mb-1'>Item</label>
            <select
              value={state.manualForm.itemId}
              onChange={event => {
                const nextItemId = event.target.value
                const nextItemOption =
                  data.stockOperationItemOptions.find(option => String(option.id) === String(nextItemId)) || null

                state.setManualForm(prev => ({
                  ...prev,
                  itemId: nextItemId,
                  qtyUnit: getDefaultInputUnit(nextItemOption?.unit || '')
                }))
              }}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
            >
              <option value=''>Seleccionar item</option>
              {data.stockOperationItemOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className='block text-xs font-semibold text-slate-700 mb-1'>Bodega</label>
            <select
              value={state.manualForm.warehouseId}
              onChange={event => state.setManualForm(prev => ({ ...prev, warehouseId: event.target.value }))}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
            >
              <option value=''>Seleccionar bodega</option>
              {data.warehouseOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
          <div>
            <label className='block text-xs font-semibold text-slate-700 mb-1'>Tipo de movimiento</label>
            <select
              value={state.manualForm.movementType}
              onChange={event => state.setManualForm(prev => ({ ...prev, movementType: event.target.value }))}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
            >
              {MANUAL_MOVEMENT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className='block text-xs font-semibold text-slate-700 mb-1'>Cantidad</label>
            <input
              type='number'
              min='0.0001'
              step='0.0001'
              value={state.manualForm.quantity}
              onChange={event => state.setManualForm(prev => ({ ...prev, quantity: event.target.value }))}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
              placeholder='Ej: 10'
            />
          </div>
          <div>
            <label className='block text-xs font-semibold text-slate-700 mb-1'>Unidad de ingreso</label>
            <select
              value={manualQtyUnit}
              onChange={event => state.setManualForm(prev => ({ ...prev, qtyUnit: event.target.value }))}
              disabled={!selectedManualItemOption}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400'
            >
              {!selectedManualItemOption && <option value=''>Seleccionar item primero</option>}
              {manualUnitOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className='text-xs text-slate-500'>
          {selectedManualItemOption
            ? `${getBaseUnitLabel(selectedManualItemOption.unit)}. El backend normaliza automáticamente la cantidad ingresada.`
            : 'Selecciona un item para ver la unidad base disponible.'}
        </p>

        <div>
          <label className='block text-xs font-semibold text-slate-700 mb-1'>Nota (opcional)</label>
          <textarea
            value={state.manualForm.note}
            onChange={event => state.setManualForm(prev => ({ ...prev, note: event.target.value }))}
            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[90px]'
            placeholder='Detalle breve del movimiento'
          />
        </div>

        <button
          type='submit'
          disabled={actions.createManualMovementMutation.isPending}
          className='w-full md:w-auto bg-[#f26c22] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#d95e1f] disabled:opacity-60'
        >
          {actions.createManualMovementMutation.isPending ? 'Guardando...' : 'Crear movimiento manual'}
        </button>
        </form>
      ) : (
        <form className='space-y-3' onSubmit={actions.handleTransferSubmit}>
        <div>
          <label className='block text-xs font-semibold text-slate-700 mb-1'>Item</label>
          <select
            value={state.transferForm.itemId}
            onChange={event => {
              const nextItemId = event.target.value
              const nextItemOption =
                data.stockOperationItemOptions.find(option => String(option.id) === String(nextItemId)) || null

              state.setTransferForm(prev => ({
                ...prev,
                itemId: nextItemId,
                qtyUnit: getDefaultInputUnit(nextItemOption?.unit || '')
              }))
            }}
            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
          >
            <option value=''>Seleccionar item</option>
            {data.stockOperationItemOptions.map(option => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <div>
            <label className='block text-xs font-semibold text-slate-700 mb-1'>Bodega origen</label>
            <select
              value={state.transferForm.fromWarehouseId}
              onChange={event => state.setTransferForm(prev => ({ ...prev, fromWarehouseId: event.target.value }))}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
            >
              <option value=''>Seleccionar origen</option>
              {data.warehouseOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className='block text-xs font-semibold text-slate-700 mb-1'>Bodega destino</label>
            <select
              value={state.transferForm.toWarehouseId}
              onChange={event => state.setTransferForm(prev => ({ ...prev, toWarehouseId: event.target.value }))}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
            >
              <option value=''>Seleccionar destino</option>
              {data.warehouseOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <div>
            <label className='block text-xs font-semibold text-slate-700 mb-1'>Cantidad</label>
            <input
              type='number'
              min='0.0001'
              step='0.0001'
              value={state.transferForm.quantity}
              onChange={event => state.setTransferForm(prev => ({ ...prev, quantity: event.target.value }))}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
              placeholder='Ej: 5'
            />
          </div>
          <div>
            <label className='block text-xs font-semibold text-slate-700 mb-1'>Unidad de ingreso</label>
            <select
              value={transferQtyUnit}
              onChange={event => state.setTransferForm(prev => ({ ...prev, qtyUnit: event.target.value }))}
              disabled={!selectedTransferItemOption}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400'
            >
              {!selectedTransferItemOption && <option value=''>Seleccionar item primero</option>}
              {transferUnitOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className='text-xs text-slate-500'>
          {selectedTransferItemOption
            ? `${getBaseUnitLabel(selectedTransferItemOption.unit)}. El backend normaliza automáticamente la cantidad ingresada.`
            : 'Selecciona un item para ver la unidad base disponible.'}
        </p>

        <div>
          <label className='block text-xs font-semibold text-slate-700 mb-1'>Nota (opcional)</label>
          <textarea
            value={state.transferForm.note}
            onChange={event => state.setTransferForm(prev => ({ ...prev, note: event.target.value }))}
            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[90px]'
            placeholder='Detalle breve de la transferencia'
          />
        </div>

        <button
          type='submit'
          disabled={actions.createTransferMutation.isPending}
          className='w-full md:w-auto bg-[#f26c22] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#d95e1f] disabled:opacity-60'
        >
          {actions.createTransferMutation.isPending ? 'Guardando...' : 'Crear transferencia'}
        </button>
        </form>
      )}
    </SectionCard>
  )
}

export default OperationsSection
